import type { DanoVeicular } from './danoVeicular'
import type { EstadoPeca, MapaEstados } from './estadoPecas'
import type { ClassificacaoDeltaV } from './metricasCinematica'
import type { TipoColisao } from './timelineCinematica'
import type { TrajetoriaFrontal } from './variacoesCinematica'

export interface RespostasInvestigador {
  direcaoImpacto: TipoColisao | null
  faixaDeltaV: ClassificacaoDeltaV | null
  prioridadesXabcde: readonly ('X' | 'A' | 'B' | 'C' | 'D' | 'E')[]
}

export interface ItemFeedbackLaudo {
  titulo: string
  acertou: boolean
  respostaUsuario: string
  gabarito: string
  explicacao: string
  pontos: number
}

export interface LaudoInvestigador {
  pontuacaoTotal: number
  classificacaoDesempenho: 'Excelente' | 'Bom' | 'Regular' | 'Insuficiente'
  laudoPericialOficial: string
  itens: readonly ItemFeedbackLaudo[]
  orientacoesCbmmg: readonly string[]
}

/**
 * Avalia as deduções do socorrista no Modo Investigador (CSI do Resgate).
 */
export function avaliarLaudoInvestigador(
  respostas: RespostasInvestigador,
  cenarioReal: {
    tipoColisao: TipoColisao
    faixaDeltaV: ClassificacaoDeltaV
    deltaVKmh: number
    prioridadesCorretas: readonly ('X' | 'A' | 'B' | 'C' | 'D' | 'E')[]
    comCinto: boolean
    trajetoriaFrontal?: TrajetoriaFrontal
  },
): LaudoInvestigador {
  const itens: ItemFeedbackLaudo[] = []
  let pontos = 0

  // 1. Direção do Impacto (40 pontos)
  const acertouDirecao = respostas.direcaoImpacto === cenarioReal.tipoColisao
  const pontosDirecao = acertouDirecao ? 40 : 0
  pontos += pontosDirecao
  itens.push({
    titulo: '1. Direção Principal do Impacto',
    acertou: acertouDirecao,
    respostaUsuario: respostas.direcaoImpacto ? respostas.direcaoImpacto.toUpperCase() : 'Não informada',
    gabarito: cenarioReal.tipoColisao.toUpperCase(),
    explicacao: acertouDirecao
      ? `Correto! As deformações estruturais e a orientação dos danos confirmam colisão ${cenarioReal.tipoColisao}.`
      : `Incorreto. A evidência de intrusão e os vetores corporais indicam que o evento foi ${cenarioReal.tipoColisao}.`,
    pontos: pontosDirecao,
  })

  // 2. Faixa de Delta-V / Energia (30 pontos)
  const acertouDeltaV = respostas.faixaDeltaV === cenarioReal.faixaDeltaV
  const pontosDeltaV = acertouDeltaV ? 30 : 0
  pontos += pontosDeltaV
  itens.push({
    titulo: '2. Estimativa de Gravidade e ΔV',
    acertou: acertouDeltaV,
    respostaUsuario: respostas.faixaDeltaV ? respostas.faixaDeltaV.toUpperCase() : 'Não informada',
    gabarito: `${cenarioReal.faixaDeltaV.toUpperCase()} (~${cenarioReal.deltaVKmh} km/h de ΔV)`,
    explicacao: acertouDeltaV
      ? `Perfeito! A intensidade dos danos e marcas de impacto é compatível com a faixa ${cenarioReal.faixaDeltaV}.`
      : `A magnitude de deformação na célula de sobrevivência e marcas corporais apontam para ΔV ${cenarioReal.faixaDeltaV} (~${cenarioReal.deltaVKmh} km/h).`,
    pontos: pontosDeltaV,
  })

  // 3. Prioridades do Protocolo XABCDE (30 pontos)
  const letrasCorretas = cenarioReal.prioridadesCorretas
  const acertosLetras = respostas.prioridadesXabcde.filter((letra) => letrasCorretas.includes(letra)).length
  const totalEsperado = Math.max(1, letrasCorretas.length)
  const pontosXabcde = Math.round((acertosLetras / totalEsperado) * 30)
  pontos += pontosXabcde
  const acertouXabcde = pontosXabcde >= 20
  itens.push({
    titulo: '3. Prioridades no Exame Primário XABCDE',
    acertou: acertouXabcde,
    respostaUsuario: respostas.prioridadesXabcde.join(', ') || 'Nenhuma selecionada',
    gabarito: letrasCorretas.join(', '),
    explicacao: `Você identificou ${acertosLetras} de ${totalEsperado} prioridades críticas da cena.`,
    pontos: pontosXabcde,
  })

  let classificacaoDesempenho: 'Excelente' | 'Bom' | 'Regular' | 'Insuficiente' = 'Insuficiente'
  if (pontos >= 85) classificacaoDesempenho = 'Excelente'
  else if (pontos >= 65) classificacaoDesempenho = 'Bom'
  else if (pontos >= 40) classificacaoDesempenho = 'Regular'

  const laudoPericialOficial = `LAUDO PERICIAL CINEMÁTICO — RESCUE-LAB
Evento analisado: Colisão ${cenarioReal.tipoColisao} com pulso líquido de ΔV medido em ${cenarioReal.deltaVKmh} km/h.
Vítima ${cenarioReal.comCinto ? 'contida com cinto de 3 pontos' : `SEM cinto de segurança (trajetória ${cenarioReal.trajetoriaFrontal ?? 'livre'})`}.
Conclusão: A cinemática impõe prioridade nos eixos ${letrasCorretas.join(', ')} do protocolo de salvamento veicular.`

  const orientacoesCbmmg = [
    'Nunca inicie o corte ou tração veicular antes de estabilizar o veículo e garantir a integridade da coluna cervical da vítima.',
    'A leitura das marcas na cabine (volante, para-brisa, pedais) antecipa o choque e a hemorragia oculta antes de a vítima descompensar.',
    'Em colisões com alto ΔV (> 40 km/h), o transporte rápido para centro de trauma é medida que salva vidas.',
  ]

  return {
    pontuacaoTotal: pontos,
    classificacaoDesempenho,
    laudoPericialOficial,
    itens,
    orientacoesCbmmg,
  }
}

/**
 * Converte o resultado da colisão cinemática em um estado correspondente de danos
 * para ser injetado diretamente na Oficina de Corte ou no Módulo de Extração.
 */
export function exportarColisaoParaOficina(
  tipoColisao: TipoColisao,
  velocidadeKmh: number,
  _dano?: DanoVeicular,
): {
  estadosPecas: MapaEstados
  descricaoExportacao: string
} {
  const estados: Record<string, EstadoPeca> = {}
  const altaEnergia = velocidadeKmh >= 40

  switch (tipoColisao) {
    case 'frontal':
    case 'frontalComTraseira':
      // Portas dianteiras emperradas estruturalmente
      estados['portaDianteiraEsquerda'] = 'destacada'
      estados['portaDianteiraDireita'] = altaEnergia ? 'destacada' : 'normal'
      estados['parabrisa'] = 'removida'
      break
    case 'lateral':
    case 'lateralContraObjeto':
      // lado do motorista (esquerdo): porta dianteira comprimida, coluna B deformada
      estados['portaDianteiraEsquerda'] = 'destacada'
      estados['portaTraseiraEsquerda'] = 'destacada'
      estados['colunaBEsquerda'] = 'destacada'
      estados['vidroLateralDianteiroEsquerdo'] = 'removida' // estilhaçado
      estados['vidroLateralTraseiroEsquerdo'] = 'removida'
      break
    case 'lateralComCapotamento':
      // o impacto é pelo lado do passageiro (direito) e o veículo capota
      estados['portaDianteiraDireita'] = 'destacada'
      estados['portaTraseiraDireita'] = 'destacada'
      estados['colunaBDireita'] = 'destacada'
      estados['vidroLateralDianteiroDireito'] = 'removida'
      estados['vidroLateralTraseiroDireito'] = 'removida'
      estados['teto'] = 'destacada'
      break
    case 'abalroamento':
      // raspagem: portas do motorista amassadas por fora, ainda operáveis na maioria dos casos
      estados['portaDianteiraEsquerda'] = altaEnergia ? 'destacada' : 'normal'
      estados['portaTraseiraEsquerda'] = altaEnergia ? 'destacada' : 'normal'
      break
    case 'capotamento':
    case 'tombamento':
      estados['portaDianteiraEsquerda'] = 'destacada'
      estados['portaDianteiraDireita'] = 'destacada'
      estados['teto'] = 'destacada'
      estados['parabrisa'] = 'removida'
      estados['vidroTraseiro'] = 'removida'
      break
    case 'traseira':
      estados['portaTraseiraEsquerda'] = 'destacada'
      estados['portaTraseiraDireita'] = 'destacada'
      estados['vidroTraseiro'] = 'removida'
      break
  }

  const descricaoExportacao = `Danos estruturais transferidos da colisão ${tipoColisao} a ${velocidadeKmh} km/h: portas travadas, vidros estilhaçados e deformações de colunas aplicados à oficina.`

  return {
    estadosPecas: estados,
    descricaoExportacao,
  }
}
